import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import type { ReactNode } from 'react';

// Home Page BI — shared band primitives (module-composable home). Each module band is a
// self-contained component whose data hook lives inside it; the parent renders a band only when
// entitled/permitted, so the gate prevents the mount rather than fetching then hiding.

const CARD = 'flex min-w-0 flex-col gap-[10px] rounded-xl border border-[#1E3049] bg-[#111E30] p-[14px_16px]';

export function BandHeader({
  tab,
  title,
  chip,
  chipTone,
  drillLabel,
  drillPath,
}: {
  tab: string;
  title: string;
  chip?: string;
  chipTone?: 'vrm' | 'compliance';
  drillLabel: string;
  drillPath: string;
}) {
  const navigate = useNavigate();
  const chipCls =
    chipTone === 'compliance'
      ? 'text-[#7FD3A8] bg-[#0F2A1F] border border-[#27543C]'
      : 'text-[#8FCDFA] bg-[#132A44] border border-[#255C8A]';
  return (
    <div className="flex flex-wrap items-center gap-[9px]">
      <span className="h-[15px] w-[3px] rounded-[2px]" style={{ background: tab }} />
      <span className="text-[15px] font-semibold tracking-[-0.01em] text-text-primary">{title}</span>
      {chip && (
        <span className={`rounded-[4px] px-[6px] py-px font-mono text-[10px] font-bold ${chipCls}`}>{chip}</span>
      )}
      <button
        onClick={() => navigate(drillPath)}
        className="ml-auto flex items-center gap-1 text-[12px] font-semibold text-accent-cyan transition-colors hover:text-text-primary"
        data-testid="band-drill"
      >
        {drillLabel} <ArrowRight className="h-3 w-3" />
      </button>
    </div>
  );
}

export function BandCards({ children }: { children: ReactNode }) {
  return (
    <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]">{children}</div>
  );
}

export function HomeCard({ cap, children }: { cap: string; children: ReactNode }) {
  return (
    <div className={CARD}>
      <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8298B0]">{cap}</span>
      {children}
    </div>
  );
}

export function BigStat({ value, unit, color = '#FFFFFF' }: { value: ReactNode; unit?: string; color?: string }) {
  return (
    <div className="flex items-baseline gap-[9px]">
      <span className="text-[28px] font-bold leading-none tracking-[-0.02em] tabular-nums" style={{ color }}>
        {value}
      </span>
      {unit && <span className="text-[12px] text-[#8298B0]">{unit}</span>}
    </div>
  );
}

export function StatPair({
  left,
  right,
}: {
  left: { n: ReactNode; l: string; color?: string };
  right: { n: ReactNode; l: string; color?: string };
}) {
  return (
    <div className="grid grid-cols-2 gap-[9px]">
      {[left, right].map((s, i) => (
        <div className="flex flex-col gap-[2px]" key={i}>
          <span className="text-[24px] font-bold leading-[1.1] tabular-nums" style={{ color: s.color ?? '#FFFFFF' }}>
            {s.n}
          </span>
          <span className="text-[10.5px] text-[#8298B0]">{s.l}</span>
        </div>
      ))}
    </div>
  );
}

export type MixSeg = { label: string; count: number; tone: string };

/** A part-to-whole composition bar + legend (never a trend). Zero-total renders nothing. */
export function MixBar({ segments }: { segments: MixSeg[] }) {
  const total = segments.reduce((s, x) => s + x.count, 0);
  return (
    <div className="flex flex-col gap-2">
      <span className="flex h-[13px] overflow-hidden rounded-[3px] bg-[#0D1626]">
        {total > 0 &&
          segments.map((s, i) => (
            <span key={i} className="block h-full" style={{ width: `${(s.count / total) * 100}%`, background: s.tone }} />
          ))}
      </span>
      <div className="flex flex-wrap items-center gap-[13px]">
        {segments.map((s, i) => (
          <span key={i} className="flex items-center gap-[6px] whitespace-nowrap text-[10.5px] text-[#9FB3C8]">
            <span className="h-[8px] w-[9px] rounded-[2px]" style={{ background: s.tone }} />
            {s.label} <span className="font-semibold tabular-nums text-[#E4EDF7]">{s.count}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export function CardHint({ children }: { children: ReactNode }) {
  return <span className="text-[10.5px] leading-[1.45] text-[#6E829B] [text-wrap:pretty]">{children}</span>;
}

export function DrillLink({ label, path }: { label: string; path: string }) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(path)}
      className="mt-auto self-start text-[11.5px] font-semibold text-accent-cyan transition-colors hover:text-text-primary"
    >
      {label}
    </button>
  );
}

/** A band that failed to load: keeps its heading + a retry line. Must look DIFFERENT from an
 *  absent (unlicensed) band, which renders nothing at all. */
export function BandError({ title, onRetry }: { title: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-[#4A2A28] bg-[#111E30] p-4" data-testid="band-error">
      <span className="text-[12.5px] font-semibold text-[#F09E99]">{title} couldn’t load.</span>
      <span className="text-[11.5px] text-[#B7C8DA]">This section is here — its data didn’t arrive.</span>
      <button onClick={onRetry} className="self-start text-[11.5px] font-semibold text-accent-cyan hover:text-text-primary">
        Retry →
      </button>
    </div>
  );
}
